import React, { useState } from 'react';
import {
  AlertTriangle,
  Clock,
  Check,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Calendar,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import {
  getBillDueStatus,
  BILL_STATUS_META,
} from 'shared/utils/billsCalendarUtils';
import { Button } from './ui/button';

export default function BillsDueRemindersBanner({
  urgentBills = [],
  onPayBill,
  formatCurrency,
  theme,
  isDark,
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (!urgentBills || urgentBills.length === 0) return null;

  const overdueCount = urgentBills.filter(
    (b) => getBillDueStatus(b, new Date()) === 'overdue'
  ).length;

  const dueTodayCount = urgentBills.filter(
    (b) => getBillDueStatus(b, new Date()) === 'due-today'
  ).length;

  const totalUrgentAmount = urgentBills.reduce(
    (sum, b) => sum + (Number(b.amount) || 0),
    0
  );

  return (
    <div
      className="mb-6 rounded-2xl border p-4 md:p-5 transition-all shadow-sm"
      style={{
        backgroundColor: overdueCount > 0
          ? (isDark ? '#3E1818' : '#FEF2F2')
          : (isDark ? '#3D2808' : '#FFFBEB'),
        borderColor: overdueCount > 0
          ? (isDark ? '#7F1D1D' : '#FCA5A5')
          : (isDark ? '#92400E' : '#FCD34D'),
      }}
    >
      {/* Banner Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: overdueCount > 0 ? '#EF4444' : '#F59E0B',
              color: '#FFFFFF',
            }}
          >
            <AlertTriangle className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                className="font-bold text-base"
                style={{
                  color: overdueCount > 0
                    ? (isDark ? '#FCA5A5' : '#991B1B')
                    : (isDark ? '#FDE68A' : '#92400E'),
                }}
              >
                {overdueCount > 0
                  ? `${overdueCount} Overdue Bill${overdueCount !== 1 ? 's' : ''}`
                  : `${dueTodayCount} Bill${dueTodayCount !== 1 ? 's' : ''} Due Today`}
              </h3>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: overdueCount > 0 ? '#EF444425' : '#F59E0B25',
                  color: overdueCount > 0 ? '#EF4444' : '#D97706',
                }}
              >
                {formatCurrency(totalUrgentAmount)} Total
              </span>
            </div>
            <p
              className="text-xs mt-0.5"
              style={{
                color: overdueCount > 0
                  ? (isDark ? '#F87171' : '#B91C1C')
                  : (isDark ? '#FBBF24' : '#B45309'),
              }}
            >
              Action required to avoid late fees or service interruptions
            </p>
          </div>
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-xs font-medium flex items-center gap-1 shrink-0"
          style={{
            color: overdueCount > 0
              ? (isDark ? '#FCA5A5' : '#991B1B')
              : (isDark ? '#FDE68A' : '#92400E'),
          }}
        >
          <span>{collapsed ? 'View Bills' : 'Collapse'}</span>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded List of Urgent Bills */}
      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-3 border-t border-black/10 dark:border-white/10">
          {urgentBills.map((bill) => {
            const status = getBillDueStatus(bill, new Date());
            const meta = BILL_STATUS_META[status] || BILL_STATUS_META.upcoming;
            const daysDiff = differenceInDays(new Date(bill.dueDate), new Date());

            let dueText = 'Due Today';
            if (daysDiff < 0) dueText = `${Math.abs(daysDiff)} day${Math.abs(daysDiff) !== 1 ? 's' : ''} overdue`;
            else if (daysDiff === 1) dueText = 'Due tomorrow';
            else if (daysDiff > 1) dueText = `Due in ${daysDiff} days`;

            return (
              <div
                key={bill.id}
                className="p-3 rounded-xl border flex items-center justify-between gap-3 shadow-sm bg-white dark:bg-[#252528]"
                style={{
                  borderColor: status === 'overdue' ? '#EF444460' : '#F59E0B60',
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate" style={{ color: theme.text }}>
                      {bill.name}
                    </span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: meta.bg, color: meta.textDark }}
                    >
                      {dueText}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                    {bill.account ? `Account: ${bill.account}` : 'No account linked'}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-sm" style={{ color: theme.text }}>
                    {formatCurrency(bill.amount)}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => onPayBill && onPayBill(bill)}
                    className="h-7 text-xs px-2.5 rounded-lg flex items-center gap-1 font-semibold"
                    style={{ backgroundColor: '#10B981', color: '#FFFFFF' }}
                  >
                    <Check className="w-3.5 h-3.5" /> Pay
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
